import React, { useState } from 'react'

import { type HomepageContent } from '../../types'
import HomeLink from '../HomeLink/HomeLink'
import styles from './styles.module.scss'

type TypicalSituationsProps = {
  content: HomepageContent['typicalSituations']
}

const TypicalSituations = ({ content }: TypicalSituationsProps) => {
  const [activeCategoryId, setActiveCategoryId] = useState(content.categories[0]?.id)
  const [expandedUseCaseIndex, setExpandedUseCaseIndex] = useState(0)
  const activeCategory =
    content.categories.find((category) => category.id === activeCategoryId) ?? content.categories[0]

  if (!activeCategory) return null

  return (
    <section
      id="typical-situations"
      className={styles.section}
      aria-labelledby="typical-situations-title"
    >
      <div className={styles.content}>
        <h2 id="typical-situations-title" className={styles.title}>
          {content.title}
        </h2>
        <div className={styles.tabs} role="tablist" aria-label="Audience">
          {content.categories.map((category) => {
            const isActive = category.id === activeCategory.id

            return (
              <button
                key={category.id}
                id={`${category.id}-tab`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${category.id}-panel`}
                className={isActive ? styles.activeTab : ''}
                onClick={() => {
                  setActiveCategoryId(category.id)
                  setExpandedUseCaseIndex(0)
                }}
              >
                {category.label}
              </button>
            )
          })}
        </div>
        <div
          id={`${activeCategory.id}-panel`}
          className={styles.card}
          role="tabpanel"
          aria-labelledby={`${activeCategory.id}-tab`}
        >
          {activeCategory.description && (
            <p className={styles.categoryDescription}>{activeCategory.description}</p>
          )}
          <ul>
            {activeCategory.useCases.map((useCase, index) => (
              <li key={useCase.title} data-expanded={expandedUseCaseIndex === index}>
                <button
                  type="button"
                  className={styles.useCaseTrigger}
                  aria-expanded={expandedUseCaseIndex === index}
                  onClick={() =>
                    setExpandedUseCaseIndex((current) => (current === index ? -1 : index))
                  }
                >
                  <span className={styles.useCaseTitle}>{useCase.title}</span>
                  <span className={styles.descriptionLabel}>Description</span>
                  <span className={styles.chevron} aria-hidden />
                </button>
                <p>{useCase.description}</p>
              </li>
            ))}
          </ul>
          <HomeLink
            className={styles.cta}
            to={activeCategory.cta.url}
            tracking={{
              event: 'homepage_typical_situation_click',
              tagName: `Homepage typical situation: ${activeCategory.label}`,
            }}
          >
            {activeCategory.cta.label}
          </HomeLink>
        </div>
      </div>
    </section>
  )
}

export default TypicalSituations
